import React, { useEffect, useRef } from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { Event } from '../../types/Event';

const FONT_DISPLAY = '"Cormorant Garamond", serif';
const FONT_BODY = '"DM Sans", sans-serif';
const FONT_MONO = '"DM Mono", monospace';

const FLYER_W = 1000;
const FLYER_H = 1056;
const PAD_H = 52;
const PAD_TOP = 40;

export interface OrganizerProfile {
   name: string;
   photoURL?: string;
   profilePicture?: string;
   avatar?: string;
   tagline?: string;
   bio?: string;
}

export interface EventFlyerLayoutProps {
   event: Event;
   organizerProfile: OrganizerProfile | null;
   publicEventUrl: string;
   qrDataUrl: string;
   eyebrow?: string;
   /** When set, used for Avatar src so html2canvas can capture it (avoids cross-origin image blocking on export) */
   hostPhotoDataUrl?: string;
   /** Called when the host avatar image has loaded (or when there is no image). Used to wait before capture. */
   onHostImageReady?: () => void;
}

function formatEventDate(start: Date | { seconds: number } | undefined, end?: Date | { seconds: number } | null) {
   if (!start) return { date: 'TBD', time: 'TBD', day: '—', month: '—' };
   const startDate = start instanceof Date ? start : new Date((start as { seconds: number }).seconds * 1000);
   const endDate = end
      ? end instanceof Date ? end : new Date((end as { seconds: number }).seconds * 1000)
      : null;
   const dateStr = startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
   const endStr = endDate
      ? endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      : null;
   const date = endStr && dateStr !== endStr ? `${dateStr} – ${endStr}` : dateStr;
   const time =
      endDate
         ? `${startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} – ${endDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`
         : startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
   const day = startDate.getDate().toString().padStart(2, '0');
   const month = startDate.toLocaleDateString('en-US', { month: 'long' }).toUpperCase();
   return { date, time, day, month };
}

function stripHtml(html: string): string {
   return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

function firstParagraph(desc: string | undefined, maxLen: number = 320): string {
   if (!desc) return '';
   const text = stripHtml(desc);
   if (text.length <= maxLen) return text;
   const cut = text.slice(0, maxLen).lastIndexOf(' ');
   return cut > 0 ? text.slice(0, cut) + '…' : text.slice(0, maxLen) + '…';
}

const EventFlyerLayout: React.FC<EventFlyerLayoutProps> = ({
   event,
   organizerProfile,
   publicEventUrl,
   qrDataUrl,
   eyebrow = 'Event',
   hostPhotoDataUrl,
   onHostImageReady,
}) => {
   const start = event.startDate;
   const end = event.endDate ?? null;
   const { date, time, day, month } = formatEventDate(
      start as any,
      end as any,
   );
   const locationName = event.location?.name || event.location?.address || 'TBD';
   const locationSub = event.location?.address && event.location?.name ? event.location.address : '';
   const accessVal = event.isPublic ? 'Public Event · Free to RSVP' : 'Private';
   const accessSub = event.isPublic ? 'Approval required · Limited spots' : 'Invite only';
   const tagline = firstParagraph(event.description);
   const hostName = organizerProfile?.name || 'Event Host';
   const hostBio = organizerProfile?.tagline || organizerProfile?.bio || '';
   const hostPhoto =
      hostPhotoDataUrl ??
      (organizerProfile?.photoURL ||
         organizerProfile?.profilePicture ||
         organizerProfile?.avatar ||
         '');
   const hostImageReadyFired = useRef(false);
   const hostImgRef = useRef<HTMLImageElement | null>(null);

   // When there is no host photo, signal ready immediately so capture doesn't wait forever.
   useEffect(() => {
      if (!hostPhoto) {
         hostImageReadyFired.current = false;
         onHostImageReady?.();
         return;
      }
      hostImageReadyFired.current = false;
   }, [hostPhoto, onHostImageReady]);

   const fireHostImageReady = () => {
      if (hostImageReadyFired.current) return;
      hostImageReadyFired.current = true;
      onHostImageReady?.();
   };

   // When we have a host photo, signal ready once the image has loaded (or is already complete e.g. cached).
   useEffect(() => {
      if (!hostPhoto || !onHostImageReady) return;
      const img = hostImgRef.current;
      if (!img) return;
      if (img.complete) fireHostImageReady();
   }, [hostPhoto, onHostImageReady]);

   return (
      <Box
         className="event-flyer-layout"
         sx={{
            width: FLYER_W,
            minHeight: FLYER_H,
            position: 'relative',
            overflow: 'visible',
            bgcolor: '#080A0F',
            display: 'flex',
            flexDirection: 'column',
         }}
      >
         {/* Background atmosphere */}
         <Box
            sx={{
               position: 'absolute',
               inset: 0,
               background: `
                  radial-gradient(ellipse 90% 50% at 10% -10%, rgba(0,196,167,0.18) 0%, transparent 55%),
                  radial-gradient(ellipse 60% 60% at 90% 80%, rgba(91,138,245,0.10) 0%, transparent 55%),
                  radial-gradient(ellipse 50% 40% at 50% 110%, rgba(0,196,167,0.08) 0%, transparent 50%)
               `,
               pointerEvents: 'none',
            }}
         />
         <Box
            sx={{
               position: 'absolute',
               inset: 0,
               backgroundImage: `
                  repeating-linear-gradient(0deg, transparent, transparent 47px, rgba(255,255,255,0.014) 48px),
                  repeating-linear-gradient(90deg, transparent, transparent 47px, rgba(255,255,255,0.014) 48px)
               `,
               pointerEvents: 'none',
            }}
         />

         {/* Top stripe */}
         <Box
            sx={{
               width: '100%',
               height: 5,
               background: 'linear-gradient(to right, #00C4A7, #00E8C8, rgba(0,196,167,0.3))',
               flexShrink: 0,
               position: 'relative',
               zIndex: 2,
            }}
         />

         {/* Header: brand left; eyebrow + date stamp side-by-side right */}
         <Box
            sx={{
               position: 'relative',
               zIndex: 2,
               pt: `${PAD_TOP}px`,
               px: `${PAD_H}px`,
               display: 'flex',
               alignItems: 'flex-start',
               justifyContent: 'space-between',
            }}
         >
            <Typography sx={{ fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
               WeTrippen
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
               {/* Eyebrow tag (event type) */}
               <Box
                  sx={{
                     fontFamily: FONT_MONO,
                     fontSize: 9,
                     letterSpacing: 3,
                     textTransform: 'uppercase',
                     color: '#00C4A7',
                     bgcolor: 'rgba(0,196,167,0.10)',
                     border: '1px solid rgba(0,196,167,0.22)',
                     px: 1.625,
                     py: 0.625,
                     borderRadius: 999,
                     display: 'flex',
                     alignItems: 'center',
                     gap: 0.875,
                  }}
               >
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#00C4A7' }} />
                  {eyebrow}
               </Box>
               {/* Date stamp (day + month) */}
               <Box
                  sx={{
                     textAlign: 'center',
                     bgcolor: 'rgba(0,196,167,0.10)',
                     border: '1px solid rgba(0,196,167,0.22)',
                     borderRadius: 1.5,
                     py: 1.25,
                     px: 1.75,
                  }}
               >
                  <Typography sx={{ fontFamily: FONT_DISPLAY, fontSize: 32, fontWeight: 300, color: '#00C4A7', lineHeight: 1 }}>
                     {day}
                  </Typography>
                  <Typography
                     sx={{
                        fontFamily: FONT_MONO,
                        fontSize: 9,
                        letterSpacing: 2,
                        textTransform: 'uppercase',
                        color: 'rgba(255,255,255,0.35)',
                        mt: 0.25,
                     }}
                  >
                     {month}
                  </Typography>
               </Box>
            </Box>
         </Box>

         {/* Title block */}
         <Box sx={{ position: 'relative', zIndex: 2, pt: 4.5, px: `${PAD_H}px` }}>
            <Typography
               component="h1"
               sx={{
                  fontFamily: FONT_DISPLAY,
                  fontSize: 90,
                  fontWeight: 300,
                  letterSpacing: -4,
                  lineHeight: 0.88,
                  color: '#fff',
                  mb: 0.75,
               }}
            >
               {event.title}
            </Typography>
            <Box
               sx={{
                  width: 80,
                  height: 2,
                  background: 'linear-gradient(to right, #00C4A7, transparent)',
                  my: 2.5,
               }}
            />
            {tagline && (
               <Typography
                  sx={{
                     fontSize: 15,
                     color: 'rgba(255,255,255,0.55)',
                     lineHeight: 1.7,
                     maxWidth: 460,
                     fontWeight: 300,
                     fontFamily: FONT_BODY,
                  }}
               >
                  {tagline}
               </Typography>
            )}
         </Box>

         {/* Divider */}
         <Box
            sx={{
               position: 'relative',
               zIndex: 2,
               mx: `${PAD_H}px`,
               my: 4.5,
               height: 1,
               bgcolor: 'rgba(255,255,255,0.07)',
            }}
         />

         {/* Body: two columns - grows with content so long description is not cut off */}
         <Box
            sx={{
               position: 'relative',
               zIndex: 2,
               px: `${PAD_H}px`,
               display: 'grid',
               gridTemplateColumns: '1fr 240px',
               gap: 5,
            }}
         >
            {/* Left: event details + about (dynamic height) */}
            <Box sx={{ pr: 0.5 }}>
               <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {[
                     {
                        icon: '📅',
                        label: 'Date',
                        value: date,
                        sub: '',
                     },
                     {
                        icon: '🕐',
                        label: 'Time',
                        value: time,
                        sub: '',
                     },
                     {
                        icon: '📍',
                        label: 'Location',
                        value: locationName,
                        sub: locationSub,
                     },
                     {
                        icon: '🌐',
                        label: 'Access',
                        value: accessVal,
                        sub: accessSub,
                     },
                  ].map((row) => (
                     <Box
                        key={row.label}
                        sx={{
                           display: 'flex',
                           alignItems: 'flex-start',
                           gap: 1.75,
                           py: 1.875,
                           borderBottom: '1px solid rgba(255,255,255,0.06)',
                        }}
                     >
                        <Box
                           sx={{
                              width: 34,
                              height: 34,
                              bgcolor: 'rgba(255,255,255,0.05)',
                              border: '1px solid rgba(255,255,255,0.08)',
                              borderRadius: 1.125,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 14,
                              flexShrink: 0,
                           }}
                        >
                           {row.icon}
                        </Box>
                        <Box>
                           <Typography
                              sx={{
                                 fontFamily: FONT_MONO,
                                 fontSize: 9,
                                 letterSpacing: 2,
                                 textTransform: 'uppercase',
                                 color: 'rgba(255,255,255,0.30)',
                                 mb: 0.375,
                              }}
                           >
                              {row.label}
                           </Typography>
                           <Typography
                              sx={{
                                 fontSize: 15,
                                 fontWeight: 600,
                                 color: '#fff',
                                 lineHeight: 1.3,
                                 fontFamily: FONT_BODY,
                              }}
                           >
                              {row.value}
                           </Typography>
                           {row.sub && (
                              <Typography
                                 sx={{
                                    fontSize: 12,
                                    color: 'rgba(255,255,255,0.35)',
                                    mt: 0.25,
                                    fontFamily: FONT_BODY,
                                 }}
                              >
                                 {row.sub}
                              </Typography>
                           )}
                        </Box>
                     </Box>
                  ))}
               </Box>
               <Box sx={{ mt: 3.5, pt: 3, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <Typography
                     sx={{
                        fontFamily: FONT_MONO,
                        fontSize: 9,
                        letterSpacing: 2.5,
                        textTransform: 'uppercase',
                        color: '#00C4A7',
                        mb: 1.25,
                     }}
                  >
                     About This Event
                  </Typography>
                  {event.description && (
                     <>
                        <Typography
                           sx={{
                              fontSize: 13,
                              color: 'rgba(255,255,255,0.50)',
                              lineHeight: 1.8,
                              fontWeight: 300,
                              fontFamily: FONT_BODY,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                           }}
                        >
                           {firstParagraph(event.description, 600)}
                        </Typography>
                        <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 1.25, p: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                           {stripHtml(event.description)
                              .split(/\n|\.\s+/)
                              .filter((s) => s.trim().length > 20)
                              .slice(0, 8)
                              .map((line, i) => (
                                 <Box key={i} component="li" sx={{ display: 'flex', gap: 1, fontSize: 12, color: 'rgba(255,255,255,0.45)', fontFamily: FONT_BODY }}>
                                    <Box component="span" sx={{ color: '#00C4A7', flexShrink: 0 }}>—</Box>
                                    {line.trim()}
                                 </Box>
                              ))}
                        </Box>
                     </>
                  )}
               </Box>
            </Box>

            {/* Right: QR + host */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
               <Box
                  sx={{
                     bgcolor: 'rgba(14,17,24,0.80)',
                     border: '1px solid rgba(255,255,255,0.10)',
                     borderRadius: 2,
                     p: 2.25,
                     textAlign: 'center',
                  }}
               >
                  <Typography
                     sx={{
                        fontFamily: FONT_MONO,
                        fontSize: 9,
                        letterSpacing: 2,
                        textTransform: 'uppercase',
                        color: 'rgba(255,255,255,0.30)',
                        mb: 1.5,
                     }}
                  >
                     Scan to RSVP
                  </Typography>
                  <Box
                     sx={{
                        bgcolor: '#fff',
                        borderRadius: 1.25,
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1.25,
                     }}
                  >
                     <Box component="img" src={qrDataUrl} alt="QR Code" sx={{ width: 148, height: 148, display: 'block' }} />
                  </Box>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11, fontWeight: 700, color: '#00C4A7' }}>
                     Scan to RSVP
                  </Typography>
                  <Typography
                     sx={{
                        fontFamily: FONT_MONO,
                        fontSize: 9,
                        color: 'rgba(255,255,255,0.25)',
                        mt: 0.375,
                        wordBreak: 'break-all',
                     }}
                  >
                     {publicEventUrl.replace(/^https?:\/\//, '')}
                  </Typography>
               </Box>
               <Box
                  sx={{
                     bgcolor: 'rgba(14,17,24,0.80)',
                     border: '1px solid rgba(255,255,255,0.10)',
                     borderRadius: 2,
                     p: 2.25,
                     textAlign: 'center',
                     position: 'relative',
                  }}
               >
                  {/* Hidden img so we can signal when host photo has loaded (for export capture) */}
                  {hostPhoto && onHostImageReady && (
                     <img
                        ref={hostImgRef}
                        src={hostPhoto}
                        alt=""
                        onLoad={fireHostImageReady}
                        onError={fireHostImageReady}
                        style={{
                           position: 'absolute',
                           width: 0,
                           height: 0,
                           opacity: 0,
                           pointerEvents: 'none',
                        }}
                        aria-hidden
                     />
                  )}
                  <Avatar
                     src={hostPhoto}
                     sx={{
                        width: 64,
                        height: 64,
                        mx: 'auto',
                        mb: 1.25,
                        bgcolor: '#00C4A7',
                        color: '#000',
                        fontFamily: FONT_DISPLAY,
                        fontSize: 26,
                        fontWeight: 600,
                        border: '2px solid rgba(0,196,167,0.4)',
                     }}
                  >
                     {hostName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography
                     sx={{
                        fontFamily: FONT_MONO,
                        fontSize: 8,
                        letterSpacing: 2,
                        textTransform: 'uppercase',
                        color: 'rgba(255,255,255,0.25)',
                        mb: 0.375,
                     }}
                  >
                     Hosted by
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#fff', mb: 0.375, fontFamily: FONT_BODY }}>
                     {hostName}
                  </Typography>
                  {hostBio && (
                     <Typography
                        sx={{
                           fontSize: 11,
                           color: 'rgba(255,255,255,0.35)',
                           lineHeight: 1.5,
                           fontStyle: 'italic',
                           fontFamily: FONT_BODY,
                        }}
                     >
                        {hostBio}
                     </Typography>
                  )}
               </Box>
            </Box>
         </Box>

         {/* Footer */}
         <Box
            sx={{
               position: 'relative',
               zIndex: 2,
               mt: 4.5,
               mx: `${PAD_H}px`,
               pt: 2.5,
               pb: 4.5,
               borderTop: '1px solid rgba(255,255,255,0.07)',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'space-between',
            }}
         >
            <Typography sx={{ fontFamily: FONT_DISPLAY, fontSize: 20, fontWeight: 300, color: '#00C4A7' }}>
               Wetrippen
            </Typography>
            <Box sx={{ textAlign: 'right' }}>
               <Typography
                  sx={{
                     fontFamily: FONT_MONO,
                     fontSize: 9,
                     letterSpacing: 2,
                     textTransform: 'uppercase',
                     color: 'rgba(255,255,255,0.18)',
                  }}
               >
                  Powered by WeTrippen
               </Typography>
               <Typography
                  sx={{
                     fontFamily: FONT_MONO,
                     fontSize: 9,
                     letterSpacing: 2,
                     textTransform: 'uppercase',
                     color: 'rgba(255,255,255,0.18)',
                  }}
               >
                  Photos delivered to your feed
               </Typography>
            </Box>
         </Box>
      </Box>
   );
};

export default EventFlyerLayout;
